/**
 * WHEN the e-commerce stores sync themselves, and what a result says.
 *
 * Pure. The timer, the network and the storage live elsewhere; this file is
 * every decision that can be wrong without anyone noticing, so it is the part
 * with tests.
 *
 * ── THIS IS A TIMER IN THE BROWSER, NOT A SCHEDULE ───────────────────────
 * The project rule is "a sync is a button, not a schedule": SWA's managed
 * Functions are HTTP-only, and a server timer would mean a metered Azure
 * resource. Mikko asked (2026-09-29) for the button not to be needed, and this
 * is the answer that stays inside the $0 plan — the app syncs the three stores
 * every 15 minutes WHILE SOMEBODY HAS i-SYNERGY OPEN.
 *
 * That is a real limit and the screen says it: nobody signed in at 3 a.m.
 * means nothing syncs at 3 a.m., and the first person in the morning catches
 * it up within seconds of opening the app. `Sync now` and `Fetch older` are
 * unchanged, and still the way to reach further back than the window below.
 */
import type { IAutoResult } from "./autoSyncStore";

export const AUTO_SYNC_INTERVAL_MS = 15 * 60_000;

/** A short window on purpose. What changes between two ticks is recent — a
 *  TikTok order moving from AWAITING_COLLECTION to DELIVERED, a Lazada item
 *  moving to SHIPPED — and a 30-day walk finishes inside one request's 45
 *  seconds where a year does not. Older history is Fetch older's job. */
export const AUTO_SYNC_WINDOW_DAYS = 30;

/** On opening the app: long enough for the shell to settle and the token to be
 *  ready, short enough that "I opened it and it caught up" is true. */
export const FIRST_RUN_DELAY_MS = 20_000;

/** Spread, so every browser that is open does not hit Shopify, TikTok and
 *  Lazada in the same second, and so two of them rarely write the same changed
 *  orders together. Also what keeps two Lazada token RENEWALS apart: Lazada
 *  rotates the refresh token, and the second of two simultaneous renewals is
 *  spending a token the first has already burned. */
export const JITTER_MS = 90_000;

/** Whether a run is owed. `lastRunAt` is epoch ms, 0 when there has never been
 *  one. A run that started 14 minutes ago by a sibling tab is not owed. */
export function isDue(now: number, lastRunAt: number): boolean {
  if (!Number.isFinite(lastRunAt) || lastRunAt <= 0) return true;
  // A clock that moved backwards must not park the timer for hours.
  if (lastRunAt > now) return true;
  return now - lastRunAt >= AUTO_SYNC_INTERVAL_MS;
}

/**
 * Milliseconds until the next tick. `jitter` is 0–1, passed in so it is
 * testable.
 *
 * Overdue (or never run) waits only the short first-run delay, because the
 * person who has just opened the app is the reason to sync at all. Otherwise it
 * waits out the rest of the interval.
 */
export function nextDelay(now: number, lastRunAt: number, jitter: number): number {
  const spread = Math.max(0, Math.min(1, jitter)) * JITTER_MS;
  if (isDue(now, lastRunAt)) return FIRST_RUN_DELAY_MS + spread;
  return Math.max(FIRST_RUN_DELAY_MS, lastRunAt + AUTO_SYNC_INTERVAL_MS - now) + spread;
}

/**
 * IG COM's stored customer key is a hash of the buyer's EMAIL for a guest
 * checkout. A caller not on IGCOM_PII_USERS is served masked emails, so the
 * same buyer would hash to a DIFFERENT key and repeat buyers would silently
 * split in two. A person pressing Sync can see that they are masked; a timer
 * cannot, so it refuses instead.
 *
 * Returns "" when it is safe to sync, otherwise the reason, in words.
 */
export function igcomBlocked(piiUnmasked: boolean): string {
  return piiUnmasked
    ? ""
    : "Skipped — this account sees masked emails, so guest customer keys would not match "
      + "the stored ones. Sync IG COM from an account that can see contact details.";
}

/** A sign-in that has lapsed reaches a Function as Azure's own token, and the
 *  Function's answer names an audience nobody recognises. Say what it means. */
export function explainError(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e ?? "");
  if (/invalid or expired token|missing bearer|azurefunctions/i.test(raw)) {
    return "Your sign-in has expired — sign out of i-Synergy and back in.";
  }
  return raw.length > 220 ? `${raw.slice(0, 217)}…` : raw || "The sync failed.";
}

const clock = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString("en-PH", { hour: "numeric", minute: "2-digit" });
};

/** The one line each screen shows for its channel. */
export function describeResult(r: IAutoResult | undefined, enabled: boolean, running: boolean): string {
  if (!enabled) return "Auto-sync paused";
  if (running) return "Auto-sync running…";
  if (!r) return "Auto-sync every 15 min — first run shortly";
  const at = clock(r.at);
  if (r.status === "failed" || r.status === "skipped") {
    return `Auto-sync ${r.status === "failed" ? "failed" : "skipped"}${at ? ` at ${at}` : ""} — ${r.note}`;
  }
  return `Auto-sync every 15 min · last ${at}`
    + (r.written > 0 ? ` · ${r.written.toLocaleString()} updated` : " · nothing new");
}
